import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PROFILE_BY_LEVEL, VAR_CEILING, riskStatus } from "@/lib/risk-policy";
import { format } from "date-fns";

interface Props {
  range: 'P' | 'R';
}

interface DailyValue {
  portfolio_level: number;
  series_type: 'portfolio' | 'benchmark';
  value_date: string;
  value: number;
}

// Periods are expressed in calendar days — the CBAM Perf sheet stores one
// row per calendar day rather than per trading day.
const PERIOD_OPTIONS: { label: string; days: number }[] = [
  { label: 'Three months', days: 90 },
  { label: 'Six months', days: 180 },
  { label: 'One year', days: 365 },
  { label: 'Three years', days: 1095 },
  { label: 'Five years', days: 1825 },
];

// Tier 1 §11.2 policy baseline: ceilings are defined for the 1-year, 95% VaR. The compliance status
// and "% of ceiling" always anchor here, regardless of the on-screen holding-period / confidence
// controls (which only drive the exploratory VaR columns). 365 calendar days = the "One year" option.
const BASELINE_HORIZON_DAYS = 365;
const BASELINE_CONFIDENCE = 0.95;

// Historical-simulation VaR for a single horizon period.
// Returns positive % loss at the given confidence (e.g. 0.95).
function computeVaR(values: number[], horizonDays: number, confidence: number): { var_pct: number; obs: number } | null {
  const rets: number[] = [];
  for (let i = 1; i < values.length; i++) {
    const a = values[i - 1], b = values[i];
    if (a > 0 && b > 0) rets.push(Math.log(b / a));
  }
  if (rets.length < horizonDays) return null;
  const horizonReturns: number[] = [];
  for (let i = horizonDays; i <= rets.length; i++) {
    let s = 0;
    for (let j = i - horizonDays; j < i; j++) s += rets[j];
    horizonReturns.push(Math.expm1(s));
  }
  if (!horizonReturns.length) return null;
  horizonReturns.sort((a, b) => a - b);
  const idx = Math.floor((1 - confidence) * horizonReturns.length);
  const worst = horizonReturns[Math.max(0, idx)];
  return { var_pct: Math.max(0, -worst) * 100, obs: horizonReturns.length };
}

async function fetchAllDaily(range: 'P' | 'R'): Promise<DailyValue[]> {
  const all: DailyValue[] = [];
  const pageSize = 1000;
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from('risk_daily_values')
      .select('portfolio_level, series_type, value_date, value')
      .eq('portfolio_range', range)
      .order('value_date', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...(data as DailyValue[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

export default function VaRPanel({ range }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [horizonDays, setHorizonDays] = useState<number>(365);
  const [confidencePct, setConfidencePct] = useState<number>(95);

  const { data: daily, isLoading } = useQuery({
    queryKey: ['risk-daily', range],
    queryFn: () => fetchAllDaily(range),
  });

  const refresh = useMutation({
    mutationFn: async () => {
      setRefreshing(true);
      const { data, error } = await supabase.functions.invoke('sync-risk-daily-values', { body: {} });
      if (error) throw error;
      if (data && (data as any).success === false) throw new Error((data as any).error || 'Sync failed');
      return data;
    },
    onSuccess: (d: any) => {
      toast({ title: 'Risk data refreshed', description: `${d?.pointsUpserted ?? 0} daily values synced from CBAM Perf` });
      qc.invalidateQueries({ queryKey: ['risk-daily'] });
    },
    onError: (e: any) => toast({ title: 'Refresh failed', description: e.message, variant: 'destructive' }),
    onSettled: () => setRefreshing(false),
  });

  const confidence = Math.min(0.999, Math.max(0.5, confidencePct / 100));

  const rows = useMemo(() => {
    return [1, 2, 3, 4, 5].map(level => {
      const profile = PROFILE_BY_LEVEL[level];
      const ceiling = VAR_CEILING[profile];
      const series = (type: 'portfolio' | 'benchmark') =>
        (daily ?? [])
          .filter(d => d.portfolio_level === level && d.series_type === type)
          .map(d => Number(d.value));
      const portSeries = series('portfolio');
      const benchSeries = series('benchmark');
      // Displayed VaR follows the holding-period / confidence controls.
      const pVar = computeVaR(portSeries, horizonDays, confidence);
      const bVar = computeVaR(benchSeries, horizonDays, confidence);
      const portfolio = pVar?.var_pct ?? null;
      const benchmark = bVar?.var_pct ?? null;
      // Compliance status & % of ceiling MUST anchor to the policy default 1yr / 95% VaR (§11.2),
      // independent of the controls above — otherwise a user could "explore" the badge to green.
      const baseVar = computeVaR(portSeries, BASELINE_HORIZON_DAYS, BASELINE_CONFIDENCE);
      const baselinePortfolio = baseVar?.var_pct ?? null;
      const status = riskStatus(baselinePortfolio, ceiling);
      const pctOfCeiling = baselinePortfolio != null ? (baselinePortfolio / ceiling) * 100 : null;
      const asOf = (daily ?? [])
        .filter(d => d.portfolio_level === level)
        .reduce<string | null>((acc, d) => (!acc || d.value_date > acc ? d.value_date : acc), null);
      return { level, profile, ceiling, portfolio, benchmark, baselinePortfolio, status, pctOfCeiling, asOf, obs: pVar?.obs ?? bVar?.obs ?? null };
    });
  }, [daily, horizonDays, confidence]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Value-at-Risk — {range} Portfolios</CardTitle>
            <CardDescription>
              Historical-simulation VaR computed from up to 3,000 daily returns per Tier 1 §11.1. Adjust the holding period and confidence level below.
            </CardDescription>
          </div>
          <Button onClick={() => refresh.mutate()} disabled={refreshing} size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh from sheet
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="space-y-1.5">
              <Label htmlFor="period" className="text-xs">Holding period</Label>
              <Select value={String(horizonDays)} onValueChange={v => setHorizonDays(Number(v))}>
                <SelectTrigger id="period" className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(o => (
                    <SelectItem key={o.days} value={String(o.days)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conf" className="text-xs">Confidence (%)</Label>
              <Input
                id="conf"
                type="number"
                step="0.1"
                min={50}
                max={99.9}
                value={confidencePct}
                onChange={e => setConfidencePct(Number(e.target.value) || 0)}
                className="w-[120px]"
              />
            </div>
            <p className="text-xs text-muted-foreground ml-auto">
              {rows[0]?.obs ? `${rows[0].obs} overlapping ${horizonDays}-day return windows` : '—'}
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio</TableHead>
                <TableHead>Risk Profile</TableHead>
                <TableHead className="text-right">Portfolio VaR (%)</TableHead>
                <TableHead className="text-right">Benchmark VaR (%)</TableHead>
                <TableHead className="text-right">Ceiling (%)</TableHead>
                <TableHead className="text-right">% of Ceiling</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data as of</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.level}>
                  <TableCell className="font-medium">{range}{r.level}</TableCell>
                  <TableCell>{r.profile}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.portfolio != null ? r.portfolio.toFixed(2) : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.benchmark != null ? r.benchmark.toFixed(2) : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.ceiling.toFixed(1)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.pctOfCeiling != null ? `${r.pctOfCeiling.toFixed(0)}%` : '—'}</TableCell>
                  <TableCell><StatusBadge status={r.status} hasData={r.baselinePortfolio != null} /></TableCell>
                  <TableCell className="text-muted-foreground">{r.asOf ? format(new Date(r.asOf), 'dd/MM/yyyy') : '—'}</TableCell>
                </TableRow>
              ))}
              {isLoading && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground mt-3">
            Note: Tier 1 §11.2 ceilings are defined for the policy default of 1-year 95% VaR. Status colouring against the ceiling remains anchored to that baseline regardless of the controls above.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Policy reference — Tier 1 §11</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>§11.1</strong> One-year 95% VaR is produced by historical simulation on 3,000 days of price data, assessed for both the portfolio and its benchmark.</p>
          <p><strong>§11.2 ceilings:</strong> Defensive 8% · Cautious 10% · Balanced 12% · Growth 16% · Aggressive 20%.</p>
          <p><strong>§11.3 alerting:</strong> ≥95% of ceiling = amber, &gt;100% = breach (red).</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status, hasData }: { status: ReturnType<typeof riskStatus>; hasData: boolean }) {
  if (!hasData) return <Badge variant="outline" className="gap-1"><ShieldQuestion className="h-3 w-3" />No data</Badge>;
  if (status === 'breach') return <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Breach</Badge>;
  if (status === 'amber') return <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-600"><ShieldAlert className="h-3 w-3" />Amber</Badge>;
  return <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Within</Badge>;
}
